import { useRoomContext } from '@livekit/components-react'
import { RoomEvent, Track } from 'livekit-client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { applySpeakerChoice, supportsAudioOutputSelection } from './audioOutput'
import {
  type DeviceKind,
  type DeviceLists,
  isDeviceSwitchInFlight,
  listDevices,
  loadDeviceChoice,
  saveDeviceChoice,
  withDeviceSwitchInFlight,
} from './devices'

export type DeviceNotice =
  | { id: number; type: 'nowUsing'; label: string }
  | { id: number; type: 'switchFailed' }
  | { id: number; type: 'deviceGone' }

export interface DevicesApi {
  devices: DeviceLists
  active: Record<DeviceKind, string>
  activeLabel: Record<DeviceKind, string>
  switching: DeviceKind | null
  notice: DeviceNotice | null
  select: (kind: DeviceKind, deviceId: string) => Promise<boolean>
  supportsSpeakerSelection: boolean
}

const EMPTY_LISTS: DeviceLists = { audioinput: [], videoinput: [], audiooutput: [] }
const EMPTY_ACTIVE: Record<DeviceKind, string> = { audioinput: '', videoinput: '', audiooutput: '' }
const DEVICECHANGE_DEBOUNCE_MS = 400

function activeTrackDeviceId(room: ReturnType<typeof useRoomContext>, kind: DeviceKind): string {
  const source = kind === 'audioinput' ? Track.Source.Microphone : Track.Source.Camera
  if (kind === 'audiooutput') return ''
  return (
    room?.localParticipant.getTrackPublication(source)?.track?.mediaStreamTrack?.getSettings()
      .deviceId ?? ''
  )
}

function activeDeviceId(room: ReturnType<typeof useRoomContext>, kind: DeviceKind): string {
  return activeTrackDeviceId(room, kind) || room?.getActiveDevice(kind) || loadDeviceChoice(kind)
}

function labelsFor(
  devices: DeviceLists,
  active: Record<DeviceKind, string>,
): Record<DeviceKind, string> {
  return {
    audioinput:
      devices.audioinput.find((device) => device.deviceId === active.audioinput)?.label ?? '',
    videoinput:
      devices.videoinput.find((device) => device.deviceId === active.videoinput)?.label ?? '',
    audiooutput:
      devices.audiooutput.find((device) => device.deviceId === active.audiooutput)?.label ?? '',
  }
}

/** In-room capture/output choices, refreshed safely as hardware changes. */
export function useDevices(recoveryRunning: boolean): DevicesApi {
  const room = useRoomContext()
  const supportsSpeakerSelection = supportsAudioOutputSelection()
  const [devices, setDevices] = useState<DeviceLists>(EMPTY_LISTS)
  const [active, setActive] = useState<Record<DeviceKind, string>>(EMPTY_ACTIVE)
  const [switching, setSwitching] = useState<DeviceKind | null>(null)
  const [notice, setNotice] = useState<DeviceNotice | null>(null)
  const previousAudioInputsRef = useRef<Set<string> | null>(null)
  const recoveryRunningRef = useRef(recoveryRunning)
  recoveryRunningRef.current = recoveryRunning
  const noticeIdRef = useRef(0)

  const publishNotice = useCallback(
    (
      next: { type: 'nowUsing'; label: string } | { type: 'switchFailed' } | { type: 'deviceGone' },
    ) => {
      noticeIdRef.current += 1
      setNotice({ ...next, id: noticeIdRef.current } as DeviceNotice)
    },
    [],
  )

  const select = useCallback(
    async (kind: DeviceKind, deviceId: string, persist = true): Promise<boolean> => {
      if (!room || recoveryRunningRef.current || isDeviceSwitchInFlight()) return false
      setSwitching(kind)
      try {
        if (kind === 'audiooutput') {
          // Use the same output-switch primitive as useSpeakerChoice, so a
          // rejected sink leaves both the stored preference and visible row alone.
          if (!(await applySpeakerChoice(room, deviceId)))
            throw new Error('audio output was not switched')
          saveDeviceChoice(kind, deviceId)
        } else {
          // `exact: false` for an empty id deliberately hands capture back to
          // the browser/OS default after an unplug, rather than asking for a
          // literal device whose id is the empty string.
          const changed = await withDeviceSwitchInFlight(() =>
            room.switchActiveDevice(kind, deviceId, Boolean(deviceId)),
          )
          if (changed === undefined) return false
          if (!changed) throw new Error('input was not switched')
          if (persist) saveDeviceChoice(kind, deviceId)
        }

        const listed = await listDevices()
        const nextActive = {
          audioinput: activeDeviceId(room, 'audioinput'),
          videoinput: activeDeviceId(room, 'videoinput'),
          audiooutput: activeDeviceId(room, 'audiooutput'),
        }
        if (kind === 'audiooutput') nextActive.audiooutput = deviceId
        setDevices(listed)
        setActive(nextActive)
        const label = labelsFor(listed, nextActive)[kind]
        publishNotice({ type: 'nowUsing', label })
        return true
      } catch {
        publishNotice({ type: 'switchFailed' })
        return false
      } finally {
        setSwitching(null)
      }
    },
    [publishNotice, room],
  )

  const refresh = useCallback(
    async (initial = false) => {
      const listed = await listDevices()
      if (!room) {
        setDevices(listed)
        return
      }

      const nextActive = {
        audioinput: activeDeviceId(room, 'audioinput'),
        videoinput: activeDeviceId(room, 'videoinput'),
        audiooutput: activeDeviceId(room, 'audiooutput'),
      }
      setDevices(listed)
      setActive(nextActive)

      const resetSelections: Promise<boolean>[] = []
      for (const kind of ['audioinput', 'videoinput', 'audiooutput'] as const) {
        const stored = loadDeviceChoice(kind)
        if (!stored || listed[kind].some((device) => device.deviceId === stored)) continue
        saveDeviceChoice(kind, '')
        if (kind !== 'audiooutput' && !recoveryRunningRef.current)
          resetSelections.push(select(kind, '', false))
        publishNotice({ type: 'deviceGone' })
      }

      // A vanished camera and a newly connected mic may arrive in one browser
      // devicechange. Complete the reset before attempting the mic adoption; the
      // input-switch mutex otherwise drops that one chance and the new headset is
      // never considered again.
      await Promise.all(resetSelections)

      const prior = previousAudioInputsRef.current
      if (initial || !prior || loadDeviceChoice('audioinput') || recoveryRunningRef.current) {
        previousAudioInputsRef.current = new Set(listed.audioinput.map((device) => device.deviceId))
        return
      }
      const newlyAppeared = listed.audioinput.filter((device) => !prior.has(device.deviceId))
      // A single new mic is almost always the headset just plugged in. Never make
      // this a stored pin: auto-follow should continue following the OS/device.
      if (
        newlyAppeared.length === 1 &&
        !(await select('audioinput', newlyAppeared[0].deviceId, false))
      )
        return
      // Advance only after the adoption above has been attempted. A failed attempt
      // remains discoverable on the next refresh instead of being permanently
      // erased from the comparison set.
      previousAudioInputsRef.current = new Set(listed.audioinput.map((device) => device.deviceId))
    },
    [publishNotice, room, select],
  )

  useEffect(() => {
    void refresh(true)
  }, [refresh])

  useEffect(() => {
    const media = navigator.mediaDevices
    if (!media?.addEventListener) return
    let timer: ReturnType<typeof window.setTimeout> | undefined
    const onDeviceChange = () => {
      if (timer) window.clearTimeout(timer)
      timer = window.setTimeout(() => void refresh(), DEVICECHANGE_DEBOUNCE_MS)
    }
    media.addEventListener('devicechange', onDeviceChange)
    return () => {
      if (timer) window.clearTimeout(timer)
      media.removeEventListener('devicechange', onDeviceChange)
    }
  }, [refresh])

  useEffect(() => {
    if (!room) return
    const onReconnected = () => void refresh()
    room.on(RoomEvent.Reconnected, onReconnected)
    return () => {
      room.off(RoomEvent.Reconnected, onReconnected)
    }
  }, [refresh, room])

  const activeLabel = useMemo(() => labelsFor(devices, active), [active, devices])
  return useMemo(
    () => ({
      devices,
      active,
      activeLabel,
      switching,
      notice,
      select,
      supportsSpeakerSelection,
    }),
    [active, activeLabel, devices, notice, select, supportsSpeakerSelection, switching],
  )
}
